import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import { URL_REMOVAL_FLAG } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type { TemplateResponseDTO } from '@conversed/rest/src/contracts/template-response/template-response.dto';
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerEditor, type AnswerEditorHandle } from '../AnswerEditor';
import { ConversationReviewTemplateResponses } from './ConversationReviewTemplateResponses';

type Props = {
  conversationId: string;
  correctedAnswer: string;
  correctedURL: string;
  templates: TemplateResponseDTO[];
  clearTrigger: number;
  answerIsMandatory: boolean;
  urlIsInvalid: boolean;
  onAnswerEditorFocus: () => void;
  onCorrectedAnswerChange: (value: string) => void;
  onCorrectedURLChange: (value: string) => void;
  onTemplateResponseSelect: (name: string, responseId: string) => void;
  onTemplateInsert: (url: string) => void;
};

export const ConversationReviewCorrectionEditor = forwardRef<
  AnswerEditorHandle,
  Props
>(
  (
    {
      conversationId,
      correctedAnswer,
      correctedURL,
      templates,
      clearTrigger,
      answerIsMandatory,
      urlIsInvalid,
      onAnswerEditorFocus,
      onCorrectedAnswerChange,
      onCorrectedURLChange,
      onTemplateResponseSelect,
      onTemplateInsert,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    // AnswerEditor's slash-command flow needs id + name + content + url tuples; map from the DTO here
    const templateOptions = useMemo(
      () =>
        templates.map((template) => ({
          value: template._id.toString(),
          description: template.name,
          content: template.content || '',
          url: template.url || '',
        })),
      [templates],
    );

    return (
      <div className='conversation-review-modal__section conversation-review-modal__section--correction'>
        <ConversationReviewTemplateResponses
          templates={templates}
          clearTrigger={clearTrigger}
          onSelect={onTemplateResponseSelect}
        />

        <div className='conversation-review-modal__correction-answer'>
          <div className='conversation-review-modal__section-label'>
            <MaterialIcon
              type='outlined'
              icon='edit'
              classes={['conversation-review-modal__section-label-icon']}
            />
            <span className='conversation-review-modal__section-label-text'>
              {t('train-ai.fact-checking.write-correction')}
            </span>
          </div>
          <div
            className={`conversation-review-modal__correction-answer-frame${answerIsMandatory ? ' conversation-review-modal__correction-answer-frame--error' : ''}`}
            onFocus={onAnswerEditorFocus}>
            <AnswerEditor
              ref={ref}
              value={correctedAnswer}
              onChange={onCorrectedAnswerChange}
              constantValue={conversationId}
              templateOptions={templateOptions}
              onTemplateSelect={onTemplateInsert}
              slashDropdownVariant='compact'
            />
          </div>
        </div>

        <div className='conversation-review-modal__correction-url-field'>
          <label
            htmlFor='correction-url-input'
            className='conversation-review-modal__section-label'>
            <MaterialIcon
              type='outlined'
              icon='link'
              classes={['conversation-review-modal__section-label-icon']}
            />
            <span className='conversation-review-modal__section-label-text'>
              {t('train-ai.fact-checking.write-correction-url')}
            </span>
            <GeneralTooltip
              text={t('train-ai.fact-checking.url-info')}
              placement='top'
              className='conversation-review-modal__correction-url-info-tooltip'>
              <MaterialIcon
                type='outlined'
                icon='info'
                classes={['conversation-review-modal__section-label-info']}
              />
            </GeneralTooltip>
          </label>
          <input
            id='correction-url-input'
            type='text'
            className={`conversation-review-modal__correction-url-field-input${urlIsInvalid ? ' conversation-review-modal__correction-url-field-input--error' : ''}`}
            placeholder='https:// or http://'
            value={correctedURL?.replace(URL_REMOVAL_FLAG, '')}
            onChange={(event) => onCorrectedURLChange(event.target.value)}
          />
        </div>
      </div>
    );
  },
);

ConversationReviewCorrectionEditor.displayName =
  'ConversationReviewCorrectionEditor';
